// Group Insurance — campaign settings panel (controls the public home-page
// section + collection window + member permissions).
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save } from "lucide-react";

export default function CampaignPanel() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/insurance/admin/campaign").then(r => setC(r.data)).catch(e => toast.error(e?.response?.data?.detail || "Load failed"));
  }, []);

  if (!c) return <Card className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></Card>;

  const set = (k, v) => setC(x => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/insurance/admin/campaign", c);
      setC(data);
      toast.success("Campaign settings saved — the home page section updates immediately");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const uploadBanner = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB"); return; }
    const r = new FileReader();
    r.onload = () => set("banner_image", r.result);
    r.readAsDataURL(file);
  };

  const Toggles = [
    ["enabled", "Show the Group Insurance section on the home page"],
    ["show_in_nav", "Show “Group Insurance” in the main navigation menu"],
    ["login_enabled", "Allow members to open/submit the data collection form"],
    ["allow_edit_after_submit", "Allow members to edit their form after submission"],
  ];

  return (
    <div className="space-y-4" data-testid="gi-campaign">
      <Card className="p-5 space-y-4">
        <h4 className="font-bold text-sm">Visibility & Access</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          {Toggles.map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 rounded-lg border border-border p-3 text-sm cursor-pointer" data-testid={`gi-campaign-${k}-row`}>
              <Switch checked={!!c[k]} onCheckedChange={v => set(k, v)} data-testid={`gi-campaign-${k}`} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="max-w-xs space-y-1.5">
          <Label className="text-xs font-semibold">Last date for data collection (leave empty for no deadline)</Label>
          <Input type="date" value={c.last_date || ""} onChange={e => set("last_date", e.target.value)} data-testid="gi-campaign-last-date" />
          <p className="text-[11px] text-muted-foreground">After this date the home section shows “Data Collection Closed” and new submissions are blocked until you extend or clear the date.</p>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h4 className="font-bold text-sm">Home Page Section Content</h4>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Heading (shown large on the home page)</Label>
          <Input value={c.heading || ""} onChange={e => set("heading", e.target.value)} data-testid="gi-campaign-heading" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Description</Label>
          <Textarea rows={3} value={c.description || ""} onChange={e => set("description", e.target.value)} data-testid="gi-campaign-description" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Login button text</Label>
            <Input value={c.button_text || ""} onChange={e => set("button_text", e.target.value)} data-testid="gi-campaign-button-text" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Know More button text</Label>
            <Input value={c.know_more_button_text || ""} onChange={e => set("know_more_button_text", e.target.value)} data-testid="gi-campaign-knowmore-text" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Instructions (“Know More” content)</Label>
          <Textarea rows={6} value={c.know_more || ""} onChange={e => set("know_more", e.target.value)} data-testid="gi-campaign-know-more" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Banner image (optional, shown behind the section)</Label>
          <div className="flex items-center gap-3">
            {c.banner_image && <img src={c.banner_image} alt="banner" className="h-16 rounded-md border" data-testid="gi-campaign-banner-preview" />}
            <Input type="file" accept="image/*" onChange={e => uploadBanner(e.target.files?.[0])} className="max-w-xs" data-testid="gi-campaign-banner" />
            {c.banner_image && <Button variant="ghost" size="sm" onClick={() => set("banner_image", "")} data-testid="gi-campaign-banner-clear">Remove</Button>}
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="gi-campaign-save">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save Campaign Settings
        </Button>
      </div>
    </div>
  );
}
